"use client";

import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageAnalytics } from "@/context/AnalyticsProvider/gamePage";
import { cn } from "@/lib/utils";
import { Gamepad2 } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { CarouselIndicator } from "../Carouselndicator";
import { GameplayModal } from "./GameplayModal";

interface GameplayCarouselProps {
  mediaURLs: string[];
  gameTitle?: string;
  gameIcon?: string;
  className?: string;
  gameId?: string;
}

export const GameplayCarousel = ({
  mediaURLs,
  gameTitle,
  gameIcon,
  className,
  gameId,
}: GameplayCarouselProps) => {
  const { track } = useGamePageAnalytics();
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === mediaURLs.length ? 1 : prev + 1));
    }, 10000);
    return () => clearInterval(interval);
  }, [mediaURLs.length]);

  const handleOpenModal = useCallback(() => {
    if (gameId) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "gameplay-open",
          control: "Gameplay",
          controlType: ControlType.BUTTON,
          gameId,
          gameName: gameTitle,
          version: "v3",
        },
      });
    }
    setIsModalOpen(true);
  }, [gameId, gameTitle, track]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") handleOpenModal();
    },
    [handleOpenModal],
  );

  return (
    <>
      <GameplayModal
        mediaURLs={mediaURLs}
        gameTitle={gameTitle}
        gameIcon={gameIcon}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        gameId={gameId}
      />

      <div
        className={cn(
          "relative glass-container-border cursor-pointer",
          className,
        )}
        onClick={handleOpenModal}
        onKeyDown={handleKeyDown}
        // biome-ignore lint/a11y/useSemanticElements: use div instead of button as we have child elements not supported by <button>
        role="button"
        tabIndex={0}
      >
        {/* Grid layout to stack images on top of each other */}
        <div className="absolute inset-0 grid grid-cols-1 grid-rows-1">
          {mediaURLs.map((url, index) =>
            // ONLY WEBP AND WEBM ARE SUPPORTED TO ENSURE LOWER SIZES, CONVERT IF NEEDED
            url.endsWith(".webp") ? (
              <Image
                key={url}
                src={url}
                alt={`Gameplay ${index + 1}`}
                priority={index === currentIndex - 1}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                className={cn(
                  "col-start-1 row-start-1 object-cover rounded-[calc(1.25rem-1px)] transition-all duration-200",
                  index === currentIndex - 1
                    ? "opacity-100"
                    : "opacity-0 hidden",
                )}
              />
            ) : (
              <video
                key={url}
                src={index === currentIndex - 1 ? url : ""}
                preload="metadata"
                autoPlay
                muted
                loop
                className={cn(
                  "size-full col-start-1 row-start-1 object-cover rounded-[calc(1.25rem-1px)] transition-all duration-200",
                  index === currentIndex - 1
                    ? "opacity-100"
                    : "opacity-0 hidden",
                )}
              />
            ),
          )}
        </div>

        <div className="absolute inset-0 size-full rounded-[1.25rem] pointer-events-none bg-darken-overlay" />

        <div className="absolute left-[10%] top-[10%] text-neutral-50 font-suisse flex items-center gap-x-1.5 @4xl:flex-col @4xl:items-start">
          <Gamepad2 className="@md:size-6 size-5" />
          <p className="@md:text-lg text-sm font-semibold">Gameplay</p>
        </div>

        <CarouselIndicator
          currentIndex={currentIndex}
          total={mediaURLs.length}
          className="absolute bottom-5 right-5"
        />
      </div>
    </>
  );
};
